import { useEffect, useMemo, useRef } from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { OFFSET_MAX, OFFSET_STEP, fmtOffset } from "@/lib/lyrics";

/* Scrolling lyrics. Synced: current line highlighted + kept centred, click a line to seek. Plain: free scroll.
   `offset` (s, + = lyrics earlier) shifts the sync; `onOffset(delta)` nudges the per-song timing. */
export const LyricsPanel = ({ lyrics, time, onSeek, offset = 0, songOffset = 0, onOffset, className = "" }) => {
  const box = useRef(null);
  const synced = lyrics.lines?.length > 0;
  const t = time + offset;
  const active = useMemo(() => {
    if (!synced) return -1;
    let i = -1;
    for (let k = 0; k < lyrics.lines.length; k++) { if (lyrics.lines[k].time <= t + 0.15) i = k; else break; }
    return i;
  }, [synced, lyrics.lines, t]);
  useEffect(() => {
    if (!synced || active < 0 || !box.current) return;
    const el = box.current.querySelector(`[data-line="${active}"]`);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [active, synced]);
  const nudge = "flex h-7 w-7 items-center justify-center rounded-full border border-white/15 text-zinc-300 transition-colors hover:border-white/40 hover:text-white disabled:opacity-30";
  return (
    <div className={`flex min-h-0 flex-col ${className}`} data-testid="lyrics-panel" data-synced={synced ? "1" : "0"} data-offset={offset}>
      {synced && onOffset && (
        <div className="mb-1 flex items-center gap-2 text-[11px] text-zinc-400" data-testid="lyrics-offset-row">
          <span className="uppercase tracking-[0.18em]">Timing</span>
          <button type="button" onClick={() => onOffset(-OFFSET_STEP)} disabled={songOffset <= -OFFSET_MAX} className={nudge} data-testid="lyrics-offset-minus" title="Lyrics later (−0.5s)" aria-label="Lyrics later"><Minus size={12} /></button>
          <span className="w-12 text-center font-mono text-xs text-zinc-200" data-testid="lyrics-offset-value">{fmtOffset(offset)}</span>
          <button type="button" onClick={() => onOffset(OFFSET_STEP)} disabled={songOffset >= OFFSET_MAX} className={nudge} data-testid="lyrics-offset-plus" title="Lyrics earlier (+0.5s)" aria-label="Lyrics earlier"><Plus size={12} /></button>
          {songOffset !== 0 && <button type="button" onClick={() => onOffset(null)} className="ml-1 flex items-center gap-1 text-zinc-400 transition-colors hover:text-white" data-testid="lyrics-offset-reset" title="Reset this song's timing"><RotateCcw size={11} />Reset</button>}
          {offset !== songOffset && <span className="ml-auto text-zinc-400" data-testid="lyrics-offset-global-hint">incl. {fmtOffset(offset - songOffset)} default</span>}
        </div>
      )}
      <div ref={box} className="lyrics-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain pr-2 [mask-image:linear-gradient(180deg,transparent,black_12%,black_88%,transparent)]" data-testid="lyrics-scroll">
        {synced ? (
          <ol className="space-y-3 py-[40%]">
            {lyrics.lines.map((l, i) => (
              <li key={i} data-line={i}>
                <button type="button" onClick={() => onSeek(Math.max(0, l.time - offset))} data-testid={`lyrics-line-${i}`} data-active={i === active ? "1" : undefined}
                  className={`block w-full text-left font-heading text-xl leading-snug transition-[color,opacity,transform] duration-300 sm:text-2xl ${i === active ? "scale-[1.02] text-white" : i < active ? "text-white/35" : "text-white/55 hover:text-white/80"}`}>
                  {l.text || "♪"}
                </button>
              </li>
            ))}
          </ol>
        ) : (
          <p className="whitespace-pre-wrap py-4 font-heading text-lg leading-relaxed text-white/80 sm:text-xl" data-testid="lyrics-plain">{lyrics.plain}</p>
        )}
      </div>
    </div>
  );
};
